import { Component, type ReactNode } from 'react'

type ErrorBoundaryProps = {
  /** Renders in place of the children after a render/effect error; `retry` remounts them. */
  fallback: (retry: () => void) => ReactNode
  children: ReactNode
}

type ErrorBoundaryState = {
  generation: number
  hasError: boolean
}

/**
 * Contains render errors to the wrapped subtree instead of letting them unmount the route.
 *
 * Deliberately not Sentry.ErrorBoundary: its own captureException would double-report every crash
 * alongside the console capture integration, which already turns the console.error below into a
 * Sentry event in staging/production (and works even where Sentry is not initialized, like dev).
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { generation: 0, hasError: false }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true }
  }

  componentDidCatch(error: unknown) {
    console.error('Contained render error', error)
  }

  retry = () => {
    this.setState((state) => ({ generation: state.generation + 1, hasError: false }))
  }

  render() {
    if (this.state.hasError) return this.props.fallback(this.retry)
    // Keyed so retry remounts the crashed subtree with fresh state instead of re-rendering it.
    return (
      <div key={this.state.generation} className="contents">
        {this.props.children}
      </div>
    )
  }
}
